import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProviders, getRequests, getEntries, createRequest, approveRequest, deleteRequest, calculateDays, checkConflicts, getMyStats } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Scale, Clock, Cake, Gift, CalendarPlus, Check, Trash2, AlertCircle, Calculator, Users, ShieldAlert, AlertTriangle, TrendingUp, Calendar } from 'lucide-react';

const RequestsPage = () => {
  const { isAdmin, user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myStats, setMyStats] = useState(null);
  
  // Form state
  const [selectedProvider, setSelectedProvider] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (startDate && endDate && startDate <= endDate) {
      checkRules();
    } else {
      setCalcResult(null);
    }
  }, [startDate, endDate, selectedProvider]);

  const loadData = async () => {
    try {
      const [providersData, requestsData, entriesData] = await Promise.all([
        getProviders(),
        getRequests(),
        getEntries()
      ]);
      setProviders(providersData);
      setRequests(requestsData);
      setEntries(entriesData);
      
      // Auto-select provider if user is a provider
      if (user?.role === 'provider' && user?.provider_id) {
        setSelectedProvider(user.provider_id);
        // Load my stats for balance display
        try {
          const stats = await getMyStats();
          setMyStats(stats);
        } catch (e) {
          console.error('Error loading stats:', e);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkRules = async () => {
    if (!startDate || !endDate) return;
    
    try {
      const result = await calculateDays(startDate, endDate);
      setCalcResult(result);
      
      if (selectedProvider) {
        const conflictsResult = await checkConflicts(selectedProvider, startDate, endDate);
        setConflicts(conflictsResult.conflicts || []);
      }
    } catch (error) {
      console.error('Error checking rules:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProvider || !startDate || !endDate) {
      alert('Preencha todos os campos');
      return;
    }
    
    if (startDate > endDate) {
      alert('Data inicial maior que final');
      return;
    }
    
    try {
      await createRequest({
        provider_id: selectedProvider,
        start_date: startDate,
        end_date: endDate
      });
      
      // Reset form
      setSelectedProvider(user?.role === 'provider' ? user.provider_id : '');
      setStartDate('');
      setEndDate('');
      setCalcResult(null);
      setConflicts([]);
      
      // Reload data
      loadData();
      alert('Sinalização registrada!');
    } catch (error) {
      alert('Erro ao registrar sinalização');
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveRequest(id);
      loadData();
    } catch (error) {
      alert('Erro ao confirmar');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Excluir sinalização?')) {
      try {
        await deleteRequest(id);
        loadData();
      } catch (error) {
        alert('Erro ao excluir');
      }
    }
  };

  const getProviderName = (id) => {
    const provider = providers.find(p => p.id === id);
    return provider?.name || 'Desconhecido';
  };

  const getProviderArea = (id) => {
    const provider = providers.find(p => p.id === id);
    return provider?.area || '-';
  };

  const formatDate = (dateStr) => {
    // Parse date string directly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  };

  // Check who else is unavailable in the same period
  const getConflictsForRequest = (request) => {
    const [startYear, startMonth, startDay] = request.start_date.split('-').map(Number);
    const [endYear, endMonth, endDay] = request.end_date.split('-').map(Number);
    const reqStart = new Date(startYear, startMonth - 1, startDay);
    const reqEnd = new Date(endYear, endMonth - 1, endDay);

    const conflictingPeople = [];

    // Check confirmed entries
    entries.forEach(e => {
      if (e.provider_id === request.provider_id) return; // Skip same person
      
      const [eStartYear, eStartMonth, eStartDay] = e.start_date.split('-').map(Number);
      const [eEndYear, eEndMonth, eEndDay] = e.end_date.split('-').map(Number);
      const eStart = new Date(eStartYear, eStartMonth - 1, eStartDay);
      const eEnd = new Date(eEndYear, eEndMonth - 1, eEndDay);

      // Check overlap
      if (reqStart <= eEnd && reqEnd >= eStart) {
        const provider = providers.find(p => p.id === e.provider_id);
        if (provider) {
          conflictingPeople.push({
            name: provider.name,
            area: provider.area,
            type: 'confirmed',
            dates: `${formatDate(e.start_date)} - ${formatDate(e.end_date)}`
          });
        }
      }
    });

    // Check other pending requests
    requests.forEach(r => {
      if (r.id === request.id || r.provider_id === request.provider_id) return;
      
      const [rStartYear, rStartMonth, rStartDay] = r.start_date.split('-').map(Number);
      const [rEndYear, rEndMonth, rEndDay] = r.end_date.split('-').map(Number);
      const rStart = new Date(rStartYear, rStartMonth - 1, rStartDay);
      const rEnd = new Date(rEndYear, rEndMonth - 1, rEndDay);

      // Check overlap
      if (reqStart <= rEnd && reqEnd >= rStart) {
        const provider = providers.find(p => p.id === r.provider_id);
        if (provider) {
          conflictingPeople.push({
            name: provider.name,
            area: provider.area,
            type: 'pending',
            dates: `${formatDate(r.start_date)} - ${formatDate(r.end_date)}`
          });
        }
      }
    });

    return conflictingPeople;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 fade-in">
      <header>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Disponibilidade & Diretrizes</h2>
        <p className="text-slate-500 text-sm">Comunique seus períodos de indisponibilidade para alinhamento do fluxo operacional.</p>
      </header>

      {/* Balance Card for Partners */}
      {!isAdmin() && myStats && (
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-slate-50" data-testid="my-balance-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-slate-900">Seu Saldo de Dias</h3>
                  <p className="text-sm text-slate-500">Limite anual: {myStats.limit} dias</p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Usados</p>
                  <p className="text-2xl font-bold text-slate-700">{myStats.days_used}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Pendentes</p>
                  <p className="text-2xl font-bold text-orange-500">{myStats.days_pending}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Disponível</p>
                  <p className={`text-2xl font-bold ${
                    myStats.status === 'ESTOUROU' ? 'text-red-600' :
                    myStats.status === 'ACABANDO' ? 'text-yellow-600' : 'text-green-600'
                  }`}>{myStats.balance}</p>
                </div>
              </div>
            </div>
            {myStats.entries && myStats.entries.length > 0 && (
              <div className="mt-4 pt-4 border-t border-indigo-100">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Histórico de Indisponibilidades
                </p>
                <div className="flex flex-wrap gap-2">
                  {myStats.entries.slice(-5).map((entry, idx) => (
                    <span key={idx} className="text-xs bg-white px-2 py-1 rounded border border-slate-200">
                      {formatDate(entry.start_date)} - {formatDate(entry.end_date)} ({entry.days} dias)
                    </span>
                  ))}
                  {myStats.entries.length > 5 && (
                    <span className="text-xs text-slate-400">+{myStats.entries.length - 5} mais</span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rules Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-slate-50 border border-indigo-100 rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-lg shadow-sm text-indigo-600">
            <FileText className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-bold text-slate-900 mb-2">Diretrizes de Coordenação Operacional</h3>
            <p className="text-sm text-slate-600 mb-4">
              Para garantir o alinhamento do fluxo operacional, sugerimos que os períodos de indisponibilidade sejam comunicados com a antecedência abaixo.
            </p>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Flexibility */}
              <div className="bg-white p-4 rounded-lg border border-pink-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-1">
                  <Gift className="h-10 w-10 text-pink-50 opacity-50" />
                </div>
                <h4 className="font-semibold text-pink-600 text-sm mb-2 flex items-center gap-2">
                  <Cake className="h-4 w-4" /> Janela de Flexibilidade
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Na data do seu aniversário, não haverá expectativa de entrega ou suporte.</li>
                  <li className="text-xs text-slate-400 mt-2">Sem prejuízo ao faturamento mensal.</li>
                </ul>
              </div>

              {/* Hybrid Rule */}
              <div className="bg-white p-4 rounded-lg border border-indigo-100">
                <h4 className="font-semibold text-indigo-800 text-sm mb-2 flex items-center gap-2">
                  <Scale className="h-4 w-4" /> Regra de Cálculo Inteligente
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="border-b border-slate-50 pb-1"><strong>Feriados:</strong> Não contam como dia de folga.</li>
                  <li className="border-b border-slate-50 pb-1"><strong>Cruzou FDS:</strong> Conta <span className="text-orange-600 font-bold">Dias Corridos</span>.</li>
                  <li><strong>Mesma semana:</strong> Conta <span className="text-green-600 font-bold">Dias Úteis</span>.</li>
                </ul>
                <p className="text-xs text-slate-400 mt-2">Ex: Sex+Seg = corridos, Seg-Qua = úteis</p>
              </div>

              {/* Notice Period */}
              <div className="bg-white p-4 rounded-lg border border-indigo-100">
                <h4 className="font-semibold text-indigo-800 text-sm mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Antecedência Sugerida
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center justify-between border-b border-slate-50 pb-1">
                    <span>Períodos curtos (1-3 dias)</span>
                    <span className="font-bold text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">72h antes</span>
                  </li>
                  <li className="flex items-center justify-between pt-1">
                    <span>Períodos longos (4+ dias)</span>
                    <span className="font-bold text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">1 mês antes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Request Form */}
        <Card className="lg:col-span-1 border-slate-200 h-fit">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-indigo-600" /> Sinalizar Indisponibilidade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="request-form">
              <div>
                <Label>Parceiro</Label>
                {user?.role === 'provider' ? (
                  <div className="mt-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-700">
                    {user?.name || providers.find(p => p.id === user?.provider_id)?.name || 'Seu nome'}
                  </div>
                ) : (
                  <Select 
                    value={selectedProvider} 
                    onValueChange={setSelectedProvider}
                  >
                    <SelectTrigger className="border-slate-300" data-testid="provider-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.area})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Início</Label>
                  <Input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border-slate-300"
                    data-testid="start-date"
                    required
                  />
                </div>
                <div>
                  <Label>Fim</Label>
                  <Input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border-slate-300"
                    data-testid="end-date"
                    required
                  />
                </div>
              </div>

              {/* Rule Feedback */}
              {calcResult && (
                <div className="space-y-2">
                  <div className="p-3 rounded-lg text-xs border bg-indigo-50 text-indigo-800 border-indigo-200 flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    <span>{calcResult.days} dias a serem considerados ({calcResult.type})</span>
                  </div>
                  
                  <div className={`p-3 rounded-lg text-xs border ${
                    calcResult.rule_valid 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-orange-50 text-orange-800 border-orange-200'
                  }`}>
                    {calcResult.rule_valid 
                      ? <span><strong>Tudo certo!</strong> Comunicação dentro da antecedência sugerida.</span>
                      : <span><strong>Atenção:</strong> {calcResult.rule_message}</span>
                    }
                  </div>
                  
                  {conflicts.length > 0 && (
                    <div className="p-3 rounded-lg text-xs border bg-orange-50 text-orange-800 border-orange-200 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span><strong>Alinhamento de cronograma:</strong> Outros parceiros da mesma área estarão indisponíveis: {conflicts.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="submit-request"
              >
                Registrar Sinalização
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Pending Requests List */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="pending-requests">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="font-heading">Notificações Pendentes</CardTitle>
              <div className="flex items-center gap-3">
                {isAdmin() && (
                  <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-100 flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> Acesso Gestor
                  </div>
                )}
                <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-bold">
                  {requests.length}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase text-xs tracking-wide">
                  <tr>
                    <th className="px-6 py-3">Parceiro</th>
                    <th className="px-6 py-3">Área</th>
                    <th className="px-6 py-3">Datas</th>
                    <th className="px-6 py-3 text-center">Dias</th>
                    {isAdmin() && <th className="px-6 py-3">Conflitos no Período</th>}
                    <th className="px-6 py-3 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {requests.map(r => {
                    const requestConflicts = isAdmin() ? getConflictsForRequest(r) : [];
                    return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`request-row-${r.id}`}>
                      <td className="px-6 py-4 font-medium text-slate-900">{getProviderName(r.provider_id)}</td>
                      <td className="px-6 py-4 text-xs text-slate-500">{getProviderArea(r.provider_id)}</td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        {formatDate(r.start_date)} <br /> {formatDate(r.end_date)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bold">{r.days}</span>
                      </td>
                      {isAdmin() && (
                        <td className="px-6 py-4">
                          {requestConflicts.length > 0 ? (
                            <div className="space-y-1">
                              {requestConflicts.map((conflict, idx) => (
                                <div 
                                  key={idx} 
                                  className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                                    conflict.type === 'confirmed' 
                                      ? 'bg-red-50 text-red-700 border border-red-200' 
                                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                  }`}
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  <span className="font-medium">{conflict.name}</span>
                                  <span className="text-[10px] opacity-75">({conflict.area})</span>
                                  <span className="text-[10px] opacity-75">{conflict.dates}</span>
                                  {conflict.type === 'pending' && <span className="text-[10px]">(pendente)</span>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <Check className="h-3 w-3" /> Sem conflitos
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 text-right">
                        {isAdmin() ? (
                          <div className="flex justify-end gap-2">
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApprove(r.id)}
                              data-testid={`approve-${r.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" /> Confirmar
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                              onClick={() => handleDelete(r.id)}
                              data-testid={`delete-request-${r.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          // Provider can cancel their own pending requests
                          r.provider_id === user?.provider_id ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleDelete(r.id)}
                              data-testid={`cancel-request-${r.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Cancelar
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Aguardando confirmação</span>
                          )
                        )}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
              {requests.length === 0 && (
                <div className="p-12 text-center text-slate-400">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma notificação pendente.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RequestsPage;
